import React from 'react';

import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import type { OptionType } from '@atlaskit/select/types';

import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { filter as positionTeamFilter } from '../../../../../controllers/store/search/filters/position-team';
import { i18n } from './messages';

const formatOptionLabel = (option: OptionType) => {
	const { label, avatarUrl } = option;

	return (
		<Inline space="space.150" alignBlock="center">
			{avatarUrl && <Avatar appearance="circle" size="xxsmall" src={avatarUrl} name={label} />}
			<Text maxLines={2}>{label}</Text>
		</Inline>
	);
};

const PositionTeamFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({ filterId: positionTeamFilter.id });

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.positionFilterTeamLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.positionFilterTeamLabelUnselected),
			options: suggestedOptions,
		},
	];

	const label =
		fit === 'compact' && selectedOptions.length > 0
			? ''
			: formatMessage(i18n.positionFilterTeamLabel);

	return (
		<SelectFilter
			contentFit={fit}
			checkboxIndicators
			canClearFilter
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(i18n.positionFilterTeamPlaceholder)}
			filterOrControlId={'positionTeam'}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatOptionLabel}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: positionTeamFilter.id });
			}}
			isFilterInDropdown={isFilterInDropdown}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default PositionTeamFilter;
